import { useCallback, useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { FocusTermHighlightPluginKey } from '../extensions/FocusTermHighlight';

interface FocusTermsOptions {
  editor: Editor | null;
  initialTerms?: string[];
}

interface FocusTermsReturn {
  focusTerms: string[];
  termCounts: Record<string, number>;
  setFocusTerms: (terms: string[]) => void;
  addTerm: (term: string) => void;
  removeTerm: (term: string) => void;
  totalOccurrences: number;
}

export function useFocusTerms({
  editor,
  initialTerms = [],
}: FocusTermsOptions): FocusTermsReturn {
  const [focusTerms, setFocusTermsState] = useState<string[]>(initialTerms);
  const [termCounts, setTermCounts] = useState<Record<string, number>>({});

  // Sync terms to editor when they change
  const setFocusTerms = useCallback(
    (terms: string[]) => {
      setFocusTermsState(terms);
      if (editor) {
        editor.view.dispatch(
          editor.view.state.tr.setMeta(FocusTermHighlightPluginKey, { terms })
        );
      }
    },
    [editor]
  );

  // Add a single term
  const addTerm = useCallback(
    (term: string) => {
      if (!term.trim()) return;
      const normalizedTerm = term.trim().toLowerCase();
      if (!focusTerms.includes(normalizedTerm)) {
        setFocusTerms([...focusTerms, normalizedTerm]);
      }
    },
    [focusTerms, setFocusTerms]
  );

  // Remove a single term
  const removeTerm = useCallback(
    (term: string) => {
      setFocusTerms(focusTerms.filter((t) => t !== term.toLowerCase()));
    },
    [focusTerms, setFocusTerms]
  );

  // Calculate total occurrences
  const totalOccurrences = Object.values(termCounts).reduce((sum, count) => sum + count, 0);

  // Set up term count tracking from editor
  useEffect(() => {
    if (!editor) return;

    const updateCounts = () => {
      const counts: Record<string, number> = {};
      focusTerms.forEach((term) => {
        counts[term] = 0;
      });

      if (focusTerms.length === 0) {
        setTermCounts(counts);
        return;
      }

      // Count occurrences in the document
      const text = editor.getText().toLowerCase();
      focusTerms.forEach((term) => {
        const regex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        const matches = text.match(regex);
        counts[term] = matches ? matches.length : 0;
      });

      setTermCounts(counts);
    };

    // Initial count
    updateCounts();

    // Update on document changes
    editor.on('update', updateCounts);

    return () => {
      editor.off('update', updateCounts);
    };
  }, [editor, focusTerms]);

  // Sync initial terms to editor on mount
  useEffect(() => {
    if (editor && initialTerms.length > 0) {
      editor.view.dispatch(
        editor.view.state.tr.setMeta(FocusTermHighlightPluginKey, { terms: initialTerms })
      );
    }
  }, [editor, initialTerms]);

  return {
    focusTerms,
    termCounts,
    setFocusTerms,
    addTerm,
    removeTerm,
    totalOccurrences,
  };
}

export default useFocusTerms;
